'use memo';

import type { UiNode, UiText } from '@ory/client';
import { useMemo } from 'react';
export type AuthMethod = {
  type: 'password';
} | {
  type: 'sso';
};
export type FilteredUi = {
  messages?: UiText[];
  nodes: UiNode[];
  action?: string;
  method?: string;
};
export type AuthMethodEntry = {
  method: AuthMethod;
  ui: FilteredUi;
};
export type UseAuthMethodsResult = {
  methods: AuthMethodEntry[];
  hasMultipleMethods: boolean;
};
function isHiddenNode(node: UiNode): boolean {
  return node.attributes.node_type === 'input' && node.attributes.type === 'hidden';
}

/**
 * flow.ui.nodes を解析して認証方法ごとにフィルタ済み ui を生成する
 *
 * パスワード用: default グループ全体 + password グループ
 * SSO 用: default グループの hidden ノード (csrf_token) + 全 oidc グループノード
 */
export function useAuthMethods(ui: {
  messages?: UiText[];
  nodes: UiNode[];
  action?: string;
  method?: string;
}): UseAuthMethodsResult {
  return useMemo(() => {
    const methods: AuthMethodEntry[] = [];
    const passwordNodes = ui.nodes.filter(node => node.group === 'password');
    const oidcNodes = ui.nodes.filter(node => node.group === 'oidc');
    const defaultNodes = ui.nodes.filter(node => !node.group || node.group === 'default');
    const defaultHiddenNodes = defaultNodes.filter(isHiddenNode);
    if (passwordNodes.length > 0) {
      methods.push({
        method: {
          type: 'password'
        },
        ui: {
          ...ui,
          nodes: [...defaultNodes, ...passwordNodes]
        }
      });
    }
    if (oidcNodes.length > 0) {
      methods.push({
        method: {
          type: 'sso'
        },
        ui: {
          ...ui,
          nodes: [...defaultHiddenNodes, ...oidcNodes]
        }
      });
    }
    return {
      methods,
      hasMultipleMethods: methods.length > 1
    };
  }, [ui]);
}